import { useState, useEffect } from 'react'
import QRCode from 'qrcode'
import { X, Download, Copy, Check } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'

interface QRGeneratorModalProps {
  isOpen: boolean
  onClose: () => void
  onGenerate: (qrCode: string) => void
  type: 'TOTE' | 'LOCATION'
  id: string
  code?: string
  title?: string
}

export function QRGeneratorModal({
  isOpen,
  onClose,
  onGenerate,
  type,
  id,
  code,
  title = 'Generate QR Code'
}: QRGeneratorModalProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string>('')
  const [copied, setCopied] = useState(false)
  const qrValue = `${type}:${id}`

  useEffect(() => {
    if (isOpen && id) {
      generateQR()
    }
  }, [isOpen, id, type])

  const generateQR = async () => {
    try {
      const dataUrl = await QRCode.toDataURL(qrValue, {
        width: 300,
        margin: 2,
        color: {
          dark: '#FF6B35',
          light: '#FFFFFF'
        }
      })
      setQrDataUrl(dataUrl)
    } catch (err) {
      console.error('Error generating QR code:', err)
    }
  }

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = qrDataUrl
    link.download = `${type}-${code || id}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(qrValue)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleUse = () => {
    onGenerate(qrValue)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="bg-surface border-border max-w-md w-full mx-4">
        <CardHeader className="border-b border-border">
          <div className="flex items-center justify-between">
            <CardTitle className="text-content">{title}</CardTitle>
            <button onClick={onClose} className="text-white hover:text-content">
              <X className="h-5 w-5" />
            </button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* QR Code Display */}
            <div className="flex justify-center p-4 bg-white rounded-lg">
              {qrDataUrl && (
                <img src={qrDataUrl} alt="QR Code" className="w-64 h-64" />
              )}
            </div>

            {/* QR Value */}
            <div className="bg-surface-secondary border border-border rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs text-white mb-1">QR Code Value:</p>
                  <p className="text-sm text-content font-mono break-all">{qrValue}</p>
                </div>
                <button
                  onClick={handleCopy}
                  className="ml-2 p-2 hover:bg-surface rounded"
                  title="Copy value"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 text-white" />
                  )}
                </button>
              </div>
            </div>

            {/* Info */}
            <div className="bg-blue-500/10 border border-blue-500 rounded-lg p-3">
              <p className="text-sm text-blue-400">
                <strong>Type:</strong> {type}
              </p>
              {code && (
                <p className="text-sm text-blue-400 mt-1">
                  <strong>Code:</strong> {code}
                </p>
              )}
              <p className="text-sm text-blue-400 mt-1">
                <strong>ID:</strong> {id.slice(0, 8)}...
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button onClick={handleDownload} variant="secondary" className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button onClick={handleUse} className="flex-1">
                Use This QR Code
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
